<script setup>
// V5-P1 · 2026-05-15 (Victor) · Date+Time picker custom M1.B
// Reemplaza <input type="date"> nativo. Diseño: cristal negro + facetas + dorado.
// Estilo: Linear/Stripe calendar popover + Material Design analog clock.
import { ref, computed, watch, onBeforeUnmount } from 'vue';

const props = defineProps({
  modelValue: { type: [String, Date, null], default: null },
  placeholder: { type: String, default: 'Elegir fecha y hora' },
});
const emit = defineEmits(['update:modelValue']);

const popoverOpen = ref(false);
const triggerRef = ref(null);
const popoverRef = ref(null);

// Estado interno del picker (puede diferir del valor confirmado hasta que el usuario "Aceptar")
function parseInitial(v) {
  if (!v) {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth(), day: now.getDate(), hour: 12, minute: 0 };
  }
  const d = v instanceof Date ? v : new Date(v);
  return { year: d.getFullYear(), month: d.getMonth(), day: d.getDate(), hour: d.getHours(), minute: d.getMinutes() };
}
const state = ref(parseInitial(props.modelValue));
// Mes que se muestra en el calendar grid (puede diferir del estado seleccionado mientras navegas)
const viewYear = ref(state.value.year);
const viewMonth = ref(state.value.month);
const clockMode = ref('hour'); // 'hour' | 'minute'

watch(() => props.modelValue, v => {
  state.value = parseInitial(v);
  viewYear.value = state.value.year;
  viewMonth.value = state.value.month;
});

// === Display del trigger (fecha + hora formateadas) ===
const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const MONTHS_SHORT = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
const triggerLabel = computed(() => {
  if (!props.modelValue) return props.placeholder;
  const d = props.modelValue instanceof Date ? props.modelValue : new Date(props.modelValue);
  if (isNaN(d.getTime())) return props.placeholder;
  const day = d.getDate();
  const month = MONTHS_SHORT[d.getMonth()];
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${month} ${year}, ${hh}:${mm}`;
});

// === Calendar grid (días del mes mostrado + spillover) ===
function daysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }
function firstDayOfMonth(year, month) {
  // Lunes = 0 (formato europeo)
  const jsDay = new Date(year, month, 1).getDay(); // 0=Domingo
  return (jsDay + 6) % 7;
}
const calendarCells = computed(() => {
  const cells = [];
  const offset = firstDayOfMonth(viewYear.value, viewMonth.value);
  const dim = daysInMonth(viewYear.value, viewMonth.value);
  const prevDim = daysInMonth(viewYear.value, viewMonth.value - 1);
  // Spillover mes anterior
  for (let i = offset - 1; i >= 0; i--) cells.push({ day: prevDim - i, muted: true });
  // Mes actual
  for (let d = 1; d <= dim; d++) cells.push({ day: d, muted: false });
  // Spillover mes siguiente (hasta llenar 42 cells = 6 filas)
  let next = 1;
  while (cells.length < 42) cells.push({ day: next++, muted: true, next: true });
  return cells;
});
const today = new Date();
function isToday(cell) {
  return !cell.muted && cell.day === today.getDate() && viewMonth.value === today.getMonth() && viewYear.value === today.getFullYear();
}
function isSelected(cell) {
  return !cell.muted && cell.day === state.value.day && viewMonth.value === state.value.month && viewYear.value === state.value.year;
}
function selectDay(cell) {
  if (cell.muted) return;
  state.value = { ...state.value, year: viewYear.value, month: viewMonth.value, day: cell.day };
}
function prevMonth() {
  if (viewMonth.value === 0) { viewMonth.value = 11; viewYear.value--; } else viewMonth.value--;
}
function nextMonth() {
  if (viewMonth.value === 11) { viewMonth.value = 0; viewYear.value++; } else viewMonth.value++;
}

// === Clock ===
// Posición de los 12 números: ángulo = (i * 30°) - 90° para que 12 quede arriba
function clockNumberStyle(i, isHour = true) {
  const num = isHour ? (i === 0 ? 12 : i) : (i * 5);
  const angle = (i * 30 - 90) * Math.PI / 180;
  const radius = 95;
  const x = Math.cos(angle) * radius;
  const y = Math.sin(angle) * radius;
  return { transform: `translate(${x}px, ${y}px)`, label: num };
}
const hourNumbers = computed(() => Array.from({ length: 12 }, (_, i) => ({ idx: i, ...clockNumberStyle(i, true) })));
const minuteNumbers = computed(() => Array.from({ length: 12 }, (_, i) => ({ idx: i, ...clockNumberStyle(i, false) })));

// Ángulo de la aguja
const handAngle = computed(() => {
  if (clockMode.value === 'hour') {
    const h = state.value.hour % 12;
    return h * 30;
  }
  return state.value.minute * 6;
});

function selectHour(num) {
  const newHour = num === 12 ? (isPM.value ? 12 : 0) : (isPM.value ? num + 12 : num);
  state.value = { ...state.value, hour: newHour };
  // Auto-cambiar a modo minutos después de elegir hora
  clockMode.value = 'minute';
}
function selectMinute(num) {
  state.value = { ...state.value, minute: num * 5 };
}
function isHourSelected(label) {
  const h12 = state.value.hour % 12 || 12;
  return label === h12;
}
function isMinuteSelected(label) {
  return label === state.value.minute || (label === 0 && state.value.minute === 0);
}

// AM/PM
const isPM = computed(() => state.value.hour >= 12);
function setAMPM(isPm) {
  if (isPm && state.value.hour < 12) state.value = { ...state.value, hour: state.value.hour + 12 };
  if (!isPm && state.value.hour >= 12) state.value = { ...state.value, hour: state.value.hour - 12 };
}

// Inputs manuales hora/min
const hourInputDisplay = computed({
  get() { return String(state.value.hour % 12 || 12).padStart(2, '0'); },
  set(v) {
    let n = parseInt(v, 10) || 12;
    if (n < 1) n = 12;
    if (n > 12) n = 12;
    const newHour = n === 12 ? (isPM.value ? 12 : 0) : (isPM.value ? n + 12 : n);
    state.value = { ...state.value, hour: newHour };
  },
});
const minuteInputDisplay = computed({
  get() { return String(state.value.minute).padStart(2, '0'); },
  set(v) {
    let n = parseInt(v, 10) || 0;
    if (n < 0) n = 0;
    if (n > 59) n = 59;
    state.value = { ...state.value, minute: n };
  },
});

// === Presets ===
function presetToday() {
  const n = new Date();
  state.value = { year: n.getFullYear(), month: n.getMonth(), day: n.getDate(), hour: 12, minute: 0 };
  viewYear.value = n.getFullYear();
  viewMonth.value = n.getMonth();
}
function presetTomorrow() {
  const n = new Date(); n.setDate(n.getDate() + 1);
  state.value = { year: n.getFullYear(), month: n.getMonth(), day: n.getDate(), hour: 12, minute: 0 };
  viewYear.value = n.getFullYear();
  viewMonth.value = n.getMonth();
}
function presetPlusWeek() {
  const n = new Date(); n.setDate(n.getDate() + 7);
  state.value = { year: n.getFullYear(), month: n.getMonth(), day: n.getDate(), hour: 12, minute: 0 };
  viewYear.value = n.getFullYear();
  viewMonth.value = n.getMonth();
}
function presetPlusMonth() {
  const n = new Date(); n.setMonth(n.getMonth() + 1);
  state.value = { year: n.getFullYear(), month: n.getMonth(), day: n.getDate(), hour: 12, minute: 0 };
  viewYear.value = n.getFullYear();
  viewMonth.value = n.getMonth();
}

// === Acciones ===
function open() {
  popoverOpen.value = true;
  state.value = parseInitial(props.modelValue);
  viewYear.value = state.value.year;
  viewMonth.value = state.value.month;
  clockMode.value = 'hour';
}
function close() { popoverOpen.value = false; }
function accept() {
  const { year, month, day, hour, minute } = state.value;
  const d = new Date(year, month, day, hour, minute, 0, 0);
  emit('update:modelValue', d.toISOString());
  close();
}
function clear() {
  emit('update:modelValue', null);
  close();
}

// Click-outside close
function onDocClick(e) {
  if (!popoverOpen.value) return;
  if (popoverRef.value && popoverRef.value.contains(e.target)) return;
  if (triggerRef.value && triggerRef.value.contains(e.target)) return;
  close();
}
function attachDocClick() { document.addEventListener('mousedown', onDocClick); }
function detachDocClick() { document.removeEventListener('mousedown', onDocClick); }
watch(popoverOpen, v => { if (v) attachDocClick(); else detachDocClick(); });
onBeforeUnmount(detachDocClick);
</script>

<template>
  <div class="dpm-root">
    <button ref="triggerRef" type="button" class="dpm-trigger" :class="{ active: popoverOpen, 'has-value': !!modelValue }" @click="popoverOpen ? close() : open()">
      <span class="dpm-ico">📅</span>
      <span class="dpm-val" :class="{ placeholder: !modelValue }">{{ triggerLabel }}</span>
      <span class="dpm-caret">{{ popoverOpen ? '▴' : '▾' }}</span>
    </button>

    <div v-if="popoverOpen" ref="popoverRef" class="dpm-pop">
      <!-- HEAD: month nav -->
      <div class="dpm-head">
        <div class="dpm-month">
          <button class="dpm-arr" type="button" @click="prevMonth">‹</button>
          <span>{{ MONTHS[viewMonth] }} {{ viewYear }}</span>
          <button class="dpm-arr" type="button" @click="nextMonth">›</button>
        </div>
        <div class="dpm-today-hint">Hoy: {{ today.getDate() }} {{ MONTHS_SHORT[today.getMonth()] }}</div>
      </div>

      <!-- BODY: 2 columnas (calendar | clock) -->
      <div class="dpm-body">
        <!-- Calendario -->
        <div class="dpm-cal">
          <div class="dpm-cal-grid">
            <div class="dpm-wkd">L</div><div class="dpm-wkd">M</div><div class="dpm-wkd">X</div>
            <div class="dpm-wkd">J</div><div class="dpm-wkd">V</div><div class="dpm-wkd">S</div><div class="dpm-wkd">D</div>
            <button
              v-for="(cell, i) in calendarCells"
              :key="i"
              type="button"
              class="dpm-day"
              :class="{ muted: cell.muted, today: isToday(cell), sel: isSelected(cell) }"
              @click="selectDay(cell)"
            >{{ cell.day }}</button>
          </div>
        </div>

        <!-- Reloj analógico -->
        <div class="dpm-clock-wrap">
          <div class="dpm-clock-modes">
            <button type="button" class="dpm-mode-pill" :class="{ active: clockMode === 'hour' }" @click="clockMode = 'hour'">🕐 Hora</button>
            <button type="button" class="dpm-mode-pill" :class="{ active: clockMode === 'minute' }" @click="clockMode = 'minute'">⏱ Min</button>
          </div>

          <div class="dpm-clock">
            <template v-if="clockMode === 'hour'">
              <button
                v-for="n in hourNumbers"
                :key="`h${n.idx}`"
                type="button"
                class="dpm-clock-num"
                :class="{ sel: isHourSelected(n.label) }"
                :style="{ transform: n.transform }"
                @click="selectHour(n.label)"
              >{{ n.label }}</button>
            </template>
            <template v-else>
              <button
                v-for="n in minuteNumbers"
                :key="`m${n.idx}`"
                type="button"
                class="dpm-clock-num"
                :class="{ sel: isMinuteSelected(n.label) }"
                :style="{ transform: n.transform }"
                @click="selectMinute(n.idx)"
              >{{ String(n.label).padStart(2, '0') }}</button>
            </template>
            <div class="dpm-hand" :style="{ transform: `translate(-50%, 0) rotate(${handAngle}deg)` }"></div>
            <div class="dpm-clock-center"></div>
          </div>

          <!-- Display + AM/PM + inputs manuales -->
          <div class="dpm-time-display">
            <input class="dpm-time-input" v-model="hourInputDisplay" type="text" maxlength="2" />
            <span class="dpm-time-sep">:</span>
            <input class="dpm-time-input" v-model="minuteInputDisplay" type="text" maxlength="2" />
            <div class="dpm-ampm">
              <button type="button" class="dpm-ampm-btn" :class="{ active: !isPM }" @click="setAMPM(false)">AM</button>
              <button type="button" class="dpm-ampm-btn" :class="{ active: isPM }" @click="setAMPM(true)">PM</button>
            </div>
          </div>
        </div>
      </div>

      <!-- PRESETS -->
      <div class="dpm-presets">
        <button type="button" class="dpm-preset" @click="presetToday">Hoy</button>
        <button type="button" class="dpm-preset" @click="presetTomorrow">Mañana</button>
        <button type="button" class="dpm-preset" @click="presetPlusWeek">+ 1 semana</button>
        <button type="button" class="dpm-preset" @click="presetPlusMonth">+ 1 mes</button>
        <button type="button" class="dpm-preset dpm-preset-clear" @click="clear">Limpiar</button>
      </div>

      <!-- ACTIONS -->
      <div class="dpm-actions">
        <button type="button" class="dpm-btn-secondary" @click="close">Cancelar</button>
        <button type="button" class="dpm-btn-primary" @click="accept">✓ Aceptar</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dpm-root { position: relative; width: 100%; }

.dpm-trigger {
  width: 100%; padding: 11px 14px; border-radius: 9px;
  background: rgba(0,0,0,0.40); border: 1px solid rgba(255,255,255,0.10);
  color: #fff; font-size: 13px; font-weight: 600;
  display: flex; align-items: center; gap: 10px; cursor: pointer;
  transition: all 0.15s; font-family: inherit;
}
.dpm-trigger:hover { border-color: rgba(251,191,36,0.45); }
.dpm-trigger.active { border-color: rgba(251,191,36,0.60); background: rgba(251,191,36,0.05); }
.dpm-ico { font-size: 16px; color: #fbbf24; }
.dpm-val { flex: 1; text-align: left; }
.dpm-val.placeholder { color: rgba(255,255,255,0.35); font-weight: 500; font-style: italic; }
.dpm-caret { color: rgba(255,255,255,0.40); font-size: 11px; }

.dpm-pop {
  position: absolute; top: calc(100% + 6px); left: 0;
  z-index: 1000; width: 640px;
  background: linear-gradient(135deg, rgba(20,20,20,0.98), rgba(8,8,8,0.98));
  border: 1px solid rgba(251,191,36,0.30); border-radius: 12px;
  box-shadow: 0 20px 50px rgba(0,0,0,0.65), 0 0 0 1px rgba(251,191,36,0.10);
  padding: 14px; backdrop-filter: blur(10px);
}
.dpm-pop::before {
  content: ""; position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, transparent, #fbbf24, transparent); opacity: 0.6;
  border-radius: 12px 12px 0 0;
}

.dpm-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 4px 6px 12px;
}
.dpm-month {
  font-size: 14px; font-weight: 800; color: #fff;
  display: flex; align-items: center; gap: 6px;
}
.dpm-arr {
  width: 26px; height: 26px; border-radius: 6px;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06);
  color: #fff; cursor: pointer; font-size: 16px; line-height: 1;
  display: inline-flex; align-items: center; justify-content: center;
  font-family: inherit;
}
.dpm-arr:hover { background: rgba(251,191,36,0.10); border-color: rgba(251,191,36,0.30); }
.dpm-today-hint { font-size: 10.5px; color: rgba(255,255,255,0.45); font-weight: 600; }

.dpm-body { display: grid; grid-template-columns: 1fr 280px; gap: 16px; }

/* === Calendario === */
.dpm-cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
.dpm-wkd {
  text-align: center; font-size: 10px; font-weight: 800; letter-spacing: 1px;
  color: rgba(255,255,255,0.40); padding: 5px 0;
}
.dpm-day {
  aspect-ratio: 1; display: inline-flex; align-items: center; justify-content: center;
  font-size: 12.5px; font-weight: 600; color: rgba(255,255,255,0.85);
  border-radius: 6px; cursor: pointer; transition: all 0.12s;
  background: transparent; border: 0; font-family: inherit;
}
.dpm-day:hover { background: rgba(251,191,36,0.10); }
.dpm-day.muted { color: rgba(255,255,255,0.20); }
.dpm-day.today { background: rgba(255,255,255,0.06); color: #fff; font-weight: 800; }
.dpm-day.sel {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  color: #000; font-weight: 800;
  box-shadow: 0 2px 8px rgba(251,191,36,0.40);
}

/* === Clock === */
.dpm-clock-wrap { display: flex; flex-direction: column; align-items: center; gap: 10px; }
.dpm-clock-modes { display: flex; gap: 4px; }
.dpm-mode-pill {
  padding: 5px 12px; border-radius: 14px; cursor: pointer;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.10);
  font-size: 10.5px; font-weight: 700; color: rgba(255,255,255,0.65);
  font-family: inherit;
}
.dpm-mode-pill.active { background: rgba(251,191,36,0.15); border-color: rgba(251,191,36,0.40); color: #fbbf24; }

.dpm-clock {
  position: relative; width: 240px; height: 240px; border-radius: 50%;
  background:
    radial-gradient(circle at center, rgba(251,191,36,0.04) 0%, transparent 70%),
    linear-gradient(135deg, rgba(28,28,28,0.95), rgba(10,10,10,0.95));
  border: 2px solid rgba(251,191,36,0.25);
  box-shadow: 0 8px 24px rgba(0,0,0,0.50), inset 0 0 30px rgba(255,255,255,0.02);
}
.dpm-clock-num {
  position: absolute; width: 32px; height: 32px; left: 50%; top: 50%; margin: -16px;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 700; color: rgba(255,255,255,0.78);
  border-radius: 50%; cursor: pointer; transition: all 0.2s;
  background: transparent; border: 0; font-family: inherit;
  font-variant-numeric: tabular-nums;
}
.dpm-clock-num:hover { background: rgba(251,191,36,0.12); color: #fde68a; }
.dpm-clock-num.sel {
  background: linear-gradient(135deg, #fbbf24, #f59e0b); color: #000; font-weight: 800;
  box-shadow: 0 0 16px rgba(251,191,36,0.55);
}
.dpm-hand {
  position: absolute; left: 50%; top: 50%;
  width: 2px; height: 78px;
  background: linear-gradient(180deg, #fbbf24, rgba(251,191,36,0.50));
  transform-origin: top center;
  transform: translate(-50%, 0) rotate(0deg);
  border-radius: 2px; pointer-events: none;
  box-shadow: 0 0 8px rgba(251,191,36,0.50);
  transition: transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
}
.dpm-clock-center {
  position: absolute; left: 50%; top: 50%; width: 10px; height: 10px;
  background: #fbbf24; border-radius: 50%; transform: translate(-50%, -50%);
  box-shadow: 0 0 12px rgba(251,191,36,0.60); pointer-events: none;
}

.dpm-time-display { display: flex; align-items: center; gap: 6px; }
.dpm-time-input {
  background: rgba(0,0,0,0.45); border: 1px solid rgba(255,255,255,0.10);
  border-radius: 6px; padding: 6px 8px; color: #fff;
  font-size: 16px; font-weight: 800; text-align: center; width: 50px;
  outline: none; font-variant-numeric: tabular-nums; font-family: inherit;
}
.dpm-time-input:focus { border-color: rgba(251,191,36,0.55); }
.dpm-time-sep { color: #fbbf24; font-weight: 800; font-size: 16px; }
.dpm-ampm { display: flex; flex-direction: column; gap: 3px; margin-left: 4px; }
.dpm-ampm-btn {
  padding: 4px 10px; border-radius: 5px; cursor: pointer;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.10);
  font-size: 10px; font-weight: 800; color: rgba(255,255,255,0.65);
  font-family: inherit;
}
.dpm-ampm-btn.active { background: rgba(251,191,36,0.15); border-color: rgba(251,191,36,0.40); color: #fbbf24; }

/* === Presets === */
.dpm-presets {
  margin-top: 12px; padding-top: 10px;
  border-top: 1px solid rgba(255,255,255,0.06);
  display: flex; gap: 6px; flex-wrap: wrap;
}
.dpm-preset {
  padding: 5px 10px; border-radius: 13px; cursor: pointer;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.10);
  font-size: 10.5px; font-weight: 700; color: rgba(255,255,255,0.75);
  font-family: inherit;
}
.dpm-preset:hover { background: rgba(251,191,36,0.10); color: #fde68a; border-color: rgba(251,191,36,0.30); }
.dpm-preset-clear { background: rgba(239,68,68,0.06); border-color: rgba(239,68,68,0.20); color: #fca5a5; }
.dpm-preset-clear:hover { background: rgba(239,68,68,0.12); border-color: rgba(239,68,68,0.40); color: #fca5a5; }

/* === Actions === */
.dpm-actions { margin-top: 12px; display: flex; gap: 8px; justify-content: flex-end; }
.dpm-btn-secondary {
  padding: 8px 14px; border-radius: 7px; cursor: pointer;
  background: transparent; border: 1px solid rgba(255,255,255,0.12);
  font-size: 11.5px; font-weight: 700; color: rgba(255,255,255,0.70);
  font-family: inherit;
}
.dpm-btn-secondary:hover { background: rgba(255,255,255,0.04); color: #fff; }
.dpm-btn-primary {
  padding: 8px 16px; border-radius: 7px; cursor: pointer;
  background: linear-gradient(135deg, #fbbf24, #f59e0b); border: 0;
  font-size: 11.5px; font-weight: 800; color: #000;
  box-shadow: 0 2px 8px rgba(251,191,36,0.30);
  font-family: inherit;
}
.dpm-btn-primary:hover { box-shadow: 0 4px 14px rgba(251,191,36,0.45); }
</style>
